import { useState } from "react";

import { Badge, Button, Disclosure, Table, type Column } from "../../components";
import { formatDateTimeShort, type ReviewItem } from "../../api/types";
import { t, tDynamic } from "../../i18n/t";
import { actorLabel, isRuleActor } from "./actorLabel";
import { ItemSummary } from "./ItemSummary";
import { ResolveControl } from "./ResolveControl";
import { itemTitle, optionLabel } from "./itemTitles";
import { isInformational, isOpen, needsChoice, suggestionOf } from "./suggestions";

/**
 * T8 round-2 review fix (finding 5/preferred remediation, plan FOLLOW-UP B): the four dedicated panels
 * (COA, TAX, CONTACT, DIMENSION) only ever render their own `kind`. Any other `kind` -- `SOURCE` (T8's own
 * `journals_scope_not_granted` / `fx_convention_unverified` items), and pre-existing `VAT_LOCK` / `CHUNKING`
 * / `HYPERCARE` -- had no web panel at all: a BLOCK item of one of those kinds still counted toward
 * `blockingCount` (disabling both Approve buttons) with no control anywhere on the page to resolve it, so a
 * default-config Xero migration got stuck at mapping review with no way through except the CLI or a raw API
 * call. This is the SAME acknowledge-only shape `DimensionLoss` already renders, generalised over `kind`
 * instead of hardcoded to `DIMENSION`, so `ReviewScreen` can show and resolve every kind it does not have a
 * dedicated tab for.
 */
export function OtherReviewItems({
  items,
  tabChoices = [],
  onOpenTab = () => undefined,
  onResolve,
  disabled: disabledProp,
}: {
  items: readonly ReviewItem[];
  /** Open choices a dedicated tab resolves; listed in the SAME "Needs your choice" section, with a button. */
  tabChoices?: readonly ReviewItem[];
  onOpenTab?: (item: ReviewItem) => void;
  onResolve: (itemId: string, option: string) => void;
  /** Whole-panel switch, or a per-item one (the read-only Review screen still lets a sign-off gate be answered). */
  disabled: boolean | ((item: ReviewItem) => boolean);
}): JSX.Element | null {
  const [chosen, setChosen] = useState<Record<string, string>>({});

  if (items.length === 0 && tabChoices.length === 0) {
    return null;
  }

  // xmg-coafast: what really needs the operator comes first, under ONE heading (xmg-coafast2: the in-tab
  // choices share it); everything else is a suggestion that "Accept all suggestions" (or one click per row)
  // takes. An open non-blocking item with no options is a note, not a row to resolve.
  // BLOCK first (an answer is owed before sign-off), WARN after; stable within each severity.
  const blockFirst = (a: ReviewItem, b: ReviewItem): number =>
    (a.severity === "BLOCK" ? 0 : 1) - (b.severity === "BLOCK" ? 0 : 1);
  const choiceItems = items.filter(needsChoice).sort(blockFirst);
  const infoItems = items.filter(isInformational);
  // Open items first, then the ones already answered (stable within each group).
  const otherItems = items
    .filter((item) => !needsChoice(item) && !isInformational(item))
    .sort((a, b) => (isOpen(a) ? 0 : 1) - (isOpen(b) ? 0 : 1));

  // An open blocking row carries a red edge, so what still stops the sign-off reads at a glance.
  const rowTone = (item: ReviewItem): string | undefined =>
    isOpen(item) && item.severity === "BLOCK" ? "is-block" : undefined;

  const columns: readonly Column<ReviewItem>[] = [
    {
      key: "kind",
      header: t("review.col_key"),
      render: (item) => {
        const { title, account } = itemTitle(item);
        return (
          <span className="cellwrap itemtitle" data-testid={`other-review-row-${item.key}`}>
            {title === null ? null : <span className="itemtitle__name">{title}</span>}
            {account === null ? null : <span className="itemtitle__account">{account}</span>}
            {/* A key with no plain title keeps the raw key as its label; the message sits in its own column. */}
            <span className={title === null ? "itemtitle__rawkey mono" : "itemtitle__key mono muted"}>
              {item.kind}: {item.key}
            </span>
          </span>
        );
      },
    },
    {
      key: "severity",
      header: t("review.col_severity"),
      render: (item) =>
        // An answered item is no longer blocking anything: it reads as resolved, not as a red "Blocking".
        isOpen(item) ? (
          <Badge tone={item.severity === "BLOCK" ? "fail" : "warn"}>
            {tDynamic("severity.", item.severity)}
          </Badge>
        ) : (
          <Badge tone="pass">{t("review.badge_resolved")}</Badge>
        ),
    },
    {
      key: "message",
      header: t("review.col_message"),
      cellClassName: "cell--wide",
      render: (item) => (
        <div className="cellwrap itemmessage">
          <ItemSummary item={item} />
        </div>
      ),
    },
    {
      key: "options",
      header: t("mapping.col_options"),
      cellClassName: "cell--wide",
      render: (item) => {
        const disabled = typeof disabledProp === "function" ? disabledProp(item) : disabledProp;
        const resolvedText =
          item.resolved_at !== null && item.resolved_at !== undefined ? (
            <span className="cellwrap muted">
              {t("review.resolved_by", {
                actor: actorLabel(item.resolved_by),
                target: optionLabel(item.resolved_option ?? ""),
              })}{" "}
              {formatDateTimeShort(item.resolved_at)}
            </span>
          ) : null;
        // xmg-autoanswer: a rule's answer is a default the operator may still change while the mapping is
        // under review (the server accepts it then); a person's answer is final here.
        if (resolvedText !== null && (disabled || !isRuleActor(item.resolved_by))) {
          return resolvedText;
        }
        const options = item.options ?? [];
        const selectId = `other-review-option-${item.id}`;
        // xmg-coafast: the select starts on the producer's suggestion (never on a cancel answer), so a
        // suggested row needs no pick, only the Resolve click -- or the header's "Accept all".
        const picked = chosen[item.id] ?? suggestionOf(item) ?? item.resolved_option ?? "";
        return (
          <>
            {resolvedText === null ? null : (
              <>
                {resolvedText} <span className="muted">{t("review.rule_change_hint")}</span>{" "}
              </>
            )}
            <ResolveControl
              selectId={selectId}
              code={item.key}
              options={options}
              picked={picked}
              labelOf={optionLabel}
              disabled={disabled || options.length === 0}
              resolveDisabled={disabled || picked === ""}
              onPick={(option) => setChosen({ ...chosen, [item.id]: option })}
              onResolve={() => onResolve(item.id, picked)}
            />
          </>
        );
      },
    },
  ];

  const openOthers = otherItems.filter((item) => isOpen(item));
  const openBlockingOthers = openOthers.filter((item) => item.severity === "BLOCK").length;
  const restCounts = [
    infoItems.length === 0 ? null : t("review.rest_notes", { n: infoItems.length }),
    openOthers.length === 0 ? null : t("review.rest_open", { n: openOthers.length }),
    otherItems.length - openOthers.length === 0
      ? null
      : t("review.rest_answered", { n: otherItems.length - openOthers.length }),
  ]
    .filter((text): text is string => text !== null)
    .join(", ");

  const rest = (
    <>
      {infoItems.length === 0 ? null : (
        <section data-testid="info-items">
          <h2>{t("review.info_title")}</h2>
          <p className="muted">{t("review.caption_info")}</p>
          <ul>
            {infoItems.map((item) => (
              <li key={item.id} data-testid={`info-item-${item.key}`}>
                <ItemSummary item={item} />
              </li>
            ))}
          </ul>
        </section>
      )}
      {otherItems.length === 0 ? null : (
        <>
          <h2>{t("review.other_items_title")}</h2>
          <div className="stacktable">
            <Table
              wrap
              className="reviewrows"
              rowClassName={rowTone}
              caption={t("review.caption_other")}
              columns={columns}
              rows={otherItems}
              rowKey={(item) => item.id}
              emptyMessage={t("review.empty")}
            />
          </div>
        </>
      )}
    </>
  );

  return (
    <>
      {choiceItems.length + tabChoices.length === 0 ? null : (
        <section data-testid="needs-your-choice">
          <h2>{t("review.needs_choice_title")}</h2>
          {tabChoices.length === 0 ? null : (
            <ul className="choicelist" data-testid="needs-choice-in-tabs">
              {[...tabChoices].sort(blockFirst).map((item) => (
                <li
                  key={item.id}
                  className={`choicelist__item${item.severity === "BLOCK" ? " choicelist__item--block" : ""}`}
                >
                  <Badge tone={item.severity === "BLOCK" ? "fail" : "warn"}>
                    {tDynamic("severity.", item.severity)}
                  </Badge>
                  <div className="choicelist__text">
                    <ItemSummary item={item} />
                  </div>
                  <Button className="button--wrap" onClick={() => onOpenTab(item)}>
                    {t("review.open_tab", { code: item.key })}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {choiceItems.length === 0 ? null : (
            <div className="stacktable">
              <Table
                wrap
                className="reviewrows"
                rowClassName={rowTone}
                caption={t("review.caption_needs_choice")}
                columns={columns}
                rows={choiceItems}
                rowKey={(item) => item.id}
                emptyMessage={t("review.empty")}
              />
            </div>
          )}
        </section>
      )}
      {/* xmg-actions-first: what needs an answer is above; the notes and the answered or suggested rows are
          collapsed here, with their counts, and open on their own only when one of them still blocks. */}
      {infoItems.length === 0 && otherItems.length === 0 ? null : (
        <Disclosure
          title={t("review.rest_title")}
          counts={restCounts}
          open={openBlockingOthers > 0 ? true : undefined}
          attention={openBlockingOthers > 0}
          testId="review-rest"
        >
          {rest}
        </Disclosure>
      )}
    </>
  );
}
